import React, { useCallback, useMemo } from "react";
import { useIntl } from "react-intl";
import { Box } from "@mui/material";
import PropTypes from "prop-types";

import { SelectInput, PhoneInput, TextInput, DropdownSearchHelpInput } from "../../../common/form-controls";
import { MenuItem } from "../../../common/menu";
import { ADD_MODE, READ_MODE } from "../../../common/entity-form";
import useUserRoles, { useApprovalGroups } from "../../../common/hooks/useUserRoles";
import { reportsService } from "../../../../services/reports-service";

const USER_ROLES = {
    ADMIN: "admin",
};

const UserRolesInput = ({ value, setValue, label, disabled, error }) => {
    const { userRoles, userRolesText } = useUserRoles();

    return (
        <SelectInput
            label={label}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={disabled}
            error={error ? value?.toLowerCase() === error.value?.toLowerCase() : false}
            errorMessage={error && error.message}
        >
            {userRoles?.map((role) => (
                <MenuItem value={role.name} key={role.id}>
                    {userRolesText[role.name]}
                </MenuItem>
            ))}
        </SelectInput>
    );
};

const ApprovalGroupInput = ({ value, setValue, label, disabled, error }) => {
    const { approvalGroups, approvalGroupText } = useApprovalGroups();

    return (
        <SelectInput
            label={label}
            value={value}
            onChange={(event) => setValue(event.target.value)}
            disabled={disabled}
             error={error ? value?.toLowerCase() === error.value?.toLowerCase() : false}
            errorMessage={error && error.message}
        >
            {approvalGroups?.map((role) => (
                <MenuItem value={role.name} key={role.id}>
                    {approvalGroupText[role.name]}
                </MenuItem>
            ))}
        </SelectInput>
    );
};

const IntegrationIdInput = ({ integrationId, setIntegrationId, error, getUsers, label, disabledMultiple = false }) => {
    const intl = useIntl();
    const labelInner = intl.formatMessage({ id: "report-parameters.users-label" });

    const ids = useMemo(() => {
        if (!integrationId) {
            return []
        }

        return [integrationId]
    }, [integrationId])

    return (
        <DropdownSearchHelpInput
            ids={ids}
            setIds={(ids) => {if (ids && ids.length > 0) {
                setIntegrationId(ids[0])
            } else {
                setIntegrationId(null)
            }}}
            getData={getUsers}
            error={error ? integrationId === error.value : false}
            errorMessage={error && error.message}
            label={label || labelInner}
            disableMultiple={disabledMultiple}
        />
    );
};

const UserFormContent = ({ form, setForm, mode, validationErrors }) => {
    const intl = useIntl();
    const getUsersByIntegrId = useCallback((params, signal) => reportsService.getUsersUnifiedByIntegrIdSearchHelp(params, signal), []);
    const setField = (key, value) =>
        setForm((form) => {
            const formNew = { ...form };

            switch (key) {
                case "role": {
                    value === USER_ROLES.ADMIN && (formNew["approvalGroup"] = null);

                    formNew[key] = value;

                    break;
                }
                default: {
                    formNew[key] = value;
                }
            }

            return formNew;
        });

    const formDisabled = mode === READ_MODE;
    const idDisabled = mode !== ADD_MODE;
    const showPasswordInput = mode === ADD_MODE;

    const approvalGroupVisible = form.role !== USER_ROLES.ADMIN;

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                gap: "16px",
                marginTop: "14px",
                maxWidth: "886px",
            }}
        >
            <TextInput
                label={intl.formatMessage({ id: "user-form-page-content.username-label" })}
                value={form.username}
                onChange={(event) => setField("username", event.target.value)}
                disabled={idDisabled}
                error={validationErrors?.username?.value?.toLowerCase() === form.username?.toLowerCase()}
                errorMessage={validationErrors?.username?.message}
            />
            <IntegrationIdInput
                integrationId={form.integrationId}
                setIntegrationId={(value) => setField("integrationId", value)}
                // error={getFieldValidationError(validationErrors, "integration_id")}
                getUsers={getUsersByIntegrId}
                label={intl.formatMessage({ id: "report-parameters.users-label-by-integr-id" })}
                disabledMultiple={true}
                error={validationErrors?.integration_id}

            />

            {showPasswordInput && (
                <TextInput
                    type="password"
                    label={intl.formatMessage({ id: "user-form-page-content.password-label" })}
                    value={form.password}
                    onChange={(event) => setField("password", event.target.value)}
                    disabled={formDisabled}
                    error={validationErrors?.password?.value?.toLowerCase() === form.password?.toLowerCase()}
                    errorMessage={validationErrors?.password?.message}
                />
            )}

            <TextInput
                label={intl.formatMessage({ id: "user-form-page-content.firstname-label" })}
                value={form.firstName}
                onChange={(event) => setField("firstName", event.target.value)}
                disabled={formDisabled}
                error={validationErrors?.first_name?.value?.toLowerCase() === form.firstName?.toLowerCase()}
                errorMessage={validationErrors?.first_name?.message}
            />

            <TextInput
                label={intl.formatMessage({ id: "user-form-page-content.lastname-label" })}
                value={form.lastName}
                onChange={(event) => setField("lastName", event.target.value)}
                disabled={formDisabled}
                error={validationErrors?.last_name?.value?.toLowerCase() === form.lastName?.toLowerCase()}
                errorMessage={validationErrors?.last_name?.message}
            />

            <TextInput
                label={intl.formatMessage({ id: "user-form-page-content.middlename-label" })}
                value={form.middleName}
                onChange={(event) => setField("middleName", event.target.value)}
                disabled={formDisabled}
                error={validationErrors?.middle_name?.value?.toLowerCase() === form.middleName?.toLowerCase()}
                errorMessage={validationErrors?.middle_name?.message}
            />

            <PhoneInput
                value={form.phone}
                label={intl.formatMessage({ id: "user-form-page-content.phone-label" })}
                onChange={(value) => setField("phone", value)}
                disabled={formDisabled}
                error={validationErrors?.phone?.value === form.phone.split(" ").join("")}
                errorMessage={validationErrors?.phone?.message}
            />

            <TextInput
                label={intl.formatMessage({ id: "user-form-page-content.email-label" })}
                value={form.email}
                onChange={(event) => setField("email", event.target.value)}
                disabled={formDisabled}
                error={validationErrors?.email?.value?.toLowerCase() === form.email?.toLowerCase()}
                errorMessage={validationErrors?.email?.message}
            />

            <UserRolesInput
                value={form.role}
                setValue={(value) => setField("role", value)}
                label={intl.formatMessage({ id: "user-form-page-content.role-label" })}
                disabled={formDisabled}
                error={validationErrors?.role}
            />

            {approvalGroupVisible && (
                <ApprovalGroupInput
                    value={form.approvalGroup}
                    setValue={(value) => setField("approvalGroup", value)}
                    label={intl.formatMessage({ id: "user-form-page-content.approval-group-label" })}
                    disabled={formDisabled}
                    error={validationErrors?.approval_group}
                />
            )}
        </Box>
    );
};

UserRolesInput.propTypes = {
    value: PropTypes.string,
    setValue: PropTypes.func,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.object,
};

IntegrationIdInput.propTypes = {
    integrationId: PropTypes.string,
    setIntegrationId: PropTypes.func,
    getUsers: PropTypes.func,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.object,
    disabledMultiple: PropTypes.bool
};

ApprovalGroupInput.propTypes = {
    value: PropTypes.string,
    setValue: PropTypes.func,
    label: PropTypes.string,
    disabled: PropTypes.bool,
    error: PropTypes.object,
};

UserFormContent.propTypes = {
    form: PropTypes.shape({
        username: PropTypes.string,
        password: PropTypes.string,
        firstName: PropTypes.string,
        lastName: PropTypes.string,
        middleName: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        role: PropTypes.string,
        approvalGroup: PropTypes.string,
        integrationId: PropTypes.string,
    }),
    setForm: PropTypes.func,
    mode: PropTypes.string,
    validationErrors: PropTypes.shape({
        username: PropTypes.string,
        password: PropTypes.string,
        first_name: PropTypes.string,
        last_name: PropTypes.string,
        middle_name: PropTypes.string,
        phone: PropTypes.string,
        email: PropTypes.string,
        role: PropTypes.string,
        approval_group: PropTypes.string,
        integration_id: PropTypes.string,
    }),
};

export default UserFormContent;
